// @mui material components
import {
  Snackbar,
  Alert,
  Grid,
  Card,
  CircularProgress,
  Backdrop,
  Icon,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Button,
  TableRow,
  TableCell,
  Table,
  Divider,
  Checkbox,
  ListItemText,
  ListItem,
  Chip,
} from "@mui/material";

// Material Dashboard 2 React components
import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import MDInput from "theme/components/MDInput";

// Material Dashboard 2 React example components
import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";

import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios.js";
import DataTable from "theme/Tables/DataTable";
// import MDBadge from "theme/components/MDBadge";
import MDButton from "theme/components/MDButton";
import usePageTitle from "app/pageTitle";
// import AppConfig from "appConfig";

import {
  ResponsiveContainer,
  PieChart,
  Pie,
  LabelList,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Cell,
  Tooltip,
} from "recharts";
import { useTheme } from "@mui/material/styles";
import AgentInsights from "../components/agent_insights";
import GroupInsights from "../components/group_insights";

function LeadBucketInsightsPage() {
  const pageTitle = "Lead Bucket Insights";
  usePageTitle(pageTitle);
  const { ref } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();

  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("");
  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };

  const [pageLoaded, setPageLoaded] = useState(false);
  const [bucket, setBucket] = useState(null);
  const [insights, setInsights] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setShowLoading(true);
      await axios
        .get(`portal/leads/buckets/${ref}/insights.json`)
        .then((result) => {
          // console.log(result);
          setShowLoading(false);
          if (result.data.status === "success") {
            setBucket(result.data.data.bucket);
            setInsights(result.data.data.insights);

            setPageLoaded(true);
          } else {
            setSeverity("error");
            setErrMsg(result.data.data.message);
            setOpen(true);
          }
        })
        .catch(() => {
          setShowLoading(false);
          setSeverity("error");
          setErrMsg(
            `Unable to complete request. ${
              navigator.onLine ? "" : "Make sure you have an active internet connection"
            }`
          );
          setOpen(true);
        });
    };

    fetchData();
  }, []);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
        onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>

      <MDBox mb={5}>
        {pageLoaded && (
          <MDBox>
            <MDBox mt={0} mb={1.5} display="flex" justifyContent="space-between">
              <MDBox>
                <Button
                  color="dark"
                  size="small"
                  startIcon={<Icon>west</Icon>}
                  onClick={() => {
                    navigate(`/leads/buckets/${ref}`);
                  }}
                >
                  Back
                </Button>
              </MDBox>
              <MDBox align="right" pr={2}>
                <MDTypography variant="h6">{bucket.title}</MDTypography>
                <MDTypography variant="body2" fontSize={12}>
                  {bucket.profile}
                </MDTypography>
              </MDBox>
            </MDBox>
            <MDBox mt={1}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4} lg={4}>
                  <MDBox sx={{ height: "100%" }}>
                    <Card sx={{ height: "100%" }}>
                      <MDBox p={2} textAlign="center">
                        <MDTypography variant="caption">Conversion Speed Avg</MDTypography>
                        <MDTypography variant="h3">
                          {insights.avg_conversion_speed
                            ? `${insights.avg_conversion_speed} days`
                            : "---"}{" "}
                        </MDTypography>
                      </MDBox>
                    </Card>
                  </MDBox>
                </Grid>
                <Grid item xs={12} md={8} lg={8}>
                  <MDBox sx={{ height: "100%" }}>
                    <Grid container spacing={2}>
                      {[
                        { label: "Total", value: insights.summary.total_assigned },
                        { label: "Converted", value: insights.summary.converted },
                        { label: "Active", value: insights.summary.active },
                        // { label: "Dead", value: insights.summary.dead },
                        {
                          label: "Conversion",
                          value: `${insights.summary.conversion_rate * 100}%`,
                        },
                      ].map((item) => (
                        <Grid item xs={12} md={3} lg={3}>
                          <Card sx={{ height: "100%" }}>
                            <MDBox p={2} align="center">
                              <MDTypography variant="caption">{item.label}</MDTypography>
                              <MDTypography variant="h5" mt={2}>
                                {item.value}
                              </MDTypography>
                            </MDBox>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  </MDBox>
                </Grid>
              </Grid>
            </MDBox>
            <MDBox mt={3}>
              <Card>
                <MDBox p={2}>
                  <MDBox align="center">
                    <MDTypography variant="h6">Lead Aging</MDTypography>
                  </MDBox>
                  <MDBox mt={1}>
                    <ResponsiveContainer width="100%" height={240}>
                      <BarChart data={insights.aging} layout="vertical" barCategoryGap="10%">
                        <XAxis type="number" tick={{ fontSize: 12 }} />
                        <YAxis dataKey="label" type="category" tick={{ fontSize: 12 }} />
                        <Tooltip />
                        <Bar dataKey="count" minPointSize={5}>
                          {insights.aging.map((entry, index) => {
                            let color = theme.palette.info.main;

                            if (entry.label === "7+ days") {
                              color = theme.palette.error.main;
                            } else if (entry.label === "4 to 7 days") {
                              color = theme.palette.warning.main;
                            } else if (entry.label === "2 to 3 days") {
                              color = theme.palette.primary.main;
                            } else {
                              color = theme.palette.success.main;
                            }

                            return <Cell key={index} fill={color} />;
                          })}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </MDBox>
                </MDBox>
              </Card>
            </MDBox>
            <MDBox mt={3}>
              <MDBox>
                {insights.attributes.map((attr, i) => {
                  const hasConversions = attr.values.some((v) => v.converted > 0);
                  const colors = [
                    theme.palette.primary.main,
                    theme.palette.info.main,
                    theme.palette.success.main,
                    theme.palette.warning.main,
                    theme.palette.error.main,
                  ];

                  return (
                    <MDBox key={i} mb={2}>
                      <MDBox>
                        <MDTypography variant="h6" mb={1}>
                          {attr.attribute}
                        </MDTypography>

                        <Grid container spacing={2}>
                          <Grid item xs={12} md={4} lg={4}>
                            <Card>
                              <MDBox p={2}>
                                <MDTypography variant="caption">Spread</MDTypography>

                                <MDBox>
                                  <ResponsiveContainer width="100%" height={240}>
                                    <PieChart>
                                      <Pie
                                        data={attr.values}
                                        dataKey="total"
                                        nameKey="value"
                                        outerRadius={70}
                                        animationDuration={600}
                                        labelLine={false}
                                        label={({ name, percent, x, y }) => (
                                          <text
                                            x={x}
                                            y={y}
                                            fontSize={12}
                                            textAnchor="middle"
                                            dominantBaseline="central"
                                            fill="#666"
                                          >
                                            {`${name} (${(percent * 100).toFixed(0)}%)`}
                                          </text>
                                        )}
                                      >
                                        {attr.values.map((entry, index) => (
                                          <Cell key={index} fill={colors[index % colors.length]} />
                                        ))}
                                      </Pie>
                                      <Tooltip />
                                    </PieChart>
                                  </ResponsiveContainer>
                                </MDBox>
                              </MDBox>
                            </Card>
                          </Grid>

                          {/* <Grid item xs={12} md={4}>
                            <MDTypography variant="caption">Converted</MDTypography>

                            <MDBox height={220}>
                              {hasConversions ? (
                                <ResponsiveContainer width="100%" height="100%">
                                  <PieChart>
                                    <Pie
                                      data={attr.values}
                                      dataKey="converted"
                                      nameKey="value"
                                      outerRadius={70}
                                    >
                                      {attr.values.map((entry, index) => (
                                        <Cell key={index} fill={colors[index % colors.length]} />
                                      ))}
                                    </Pie>
                                    <Tooltip />
                                  </PieChart>
                                </ResponsiveContainer>
                              ) : (
                                <MDBox
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="center"
                                  height="100%"
                                >
                                  <MDTypography variant="caption">No conversions yet</MDTypography>
                                </MDBox>
                              )}
                            </MDBox>
                          </Grid> */}

                          <Grid item xs={12} md={8} lg={8}>
                            <Card>
                              <MDBox p={2}>
                                <MDTypography variant="caption">Conversion Rate</MDTypography>

                                <MDBox>
                                  <ResponsiveContainer width="100%" height={240}>
                                    <BarChart
                                      data={attr.values}
                                      layout="vertical"
                                      // margin={{ left: 10, right: 20 }}
                                      barCategoryGap="30%"
                                    >
                                      <XAxis
                                        type="number"
                                        domain={[0, 1]}
                                        tick={{ fontSize: 10 }}
                                        tickFormatter={(v) => `${Math.round(v * 100)}%`}
                                      />

                                      <YAxis
                                        dataKey="value"
                                        type="category"
                                        width={80}
                                        tick={{ fontSize: 12 }}
                                      />

                                      <Tooltip formatter={(v) => `${Math.round(v * 100)}%`} />

                                      <Bar dataKey="conversion_rate" barSize={8} minPointSize={5}>
                                        <LabelList
                                          dataKey="conversion_rate"
                                          position="right"
                                          formatter={(v) => `${Math.round(v * 100)}%`}
                                        />
                                      </Bar>
                                      <Tooltip />
                                    </BarChart>
                                  </ResponsiveContainer>
                                </MDBox>
                              </MDBox>
                            </Card>
                          </Grid>
                        </Grid>
                      </MDBox>
                    </MDBox>
                  );
                })}
              </MDBox>
            </MDBox>
            <MDBox mt={5}>
              <MDBox align="center">
                <MDTypography variant="h6">Team Insights</MDTypography>
              </MDBox>
              <MDBox mt={2}>
                <GroupInsights groups={insights.groups} />
              </MDBox>
              <MDBox mt={5}>
                <AgentInsights insights={insights.agents} />
              </MDBox>
            </MDBox>
          </MDBox>
        )}
      </MDBox>

      <Footer />
    </DashboardLayout>
  );
}

export default LeadBucketInsightsPage;
